import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  CheckCircle2, AlertTriangle, XCircle, HelpCircle,
  Wrench, Eye, AlertCircle, Target, Activity, ChevronDown,
  ChevronLeft, ChevronRight, LogOut, Plus, Radar, RefreshCw, X
} from 'lucide-react';

type LucideIcon = React.ComponentType<{ size?: number | string; strokeWidth?: number | string; className?: string }>;

const STATUS_ICON: Record<string, LucideIcon> = {
  operational: CheckCircle2,
  degraded:    AlertTriangle,
  down:        XCircle,
  unknown:     HelpCircle,
};

interface Service {
  id: string;
  name: string;
  url: string;
  timeoutMs?: number;
  isPublic?: boolean;
  message?: string;
  forcedStatus?: '' | 'operational' | 'maintenance' | 'monitoring' | 'degraded' | 'partial_outage' | 'investigating' | 'identified' | 'down';
}

interface HealthEntry {
  id: string;
  name: string;
  url: string;
  isPublic: boolean;
  message: string;
  forcedStatus: string;
  status: 'operational' | 'degraded' | 'down' | 'unknown';
  httpStatus: number | null;
  latency: number | null;
  checkedAt: number | null;
  detail: string | null;
  uptime24h: number | null;
  uptime1h: number | null;
}

interface Discovered {
  hostname: string;
  tracked: boolean;
}

interface ProbeLog {
  t: number;
  id: string;
  name: string;
  status: string;
  prevStatus: string | null;
  httpStatus: number | null;
  latency: number | null;
  detail: string;
  responseHeaders?: Record<string, string> | null;
  bodySnippet?: string | null;
}

type ForceOptionDef = { value: Service['forcedStatus']; label: string; Icon: LucideIcon };

const FORCE_OPTIONS: ForceOptionDef[] = [
  { value: '',               label: 'Live (auto-detect)',  Icon: Activity },
  { value: 'operational',   label: 'Operational',          Icon: CheckCircle2 },
  { value: 'maintenance',   label: 'Under Maintenance',    Icon: Wrench },
  { value: 'monitoring',    label: 'Monitoring',           Icon: Eye },
  { value: 'degraded',      label: 'Degraded Performance', Icon: AlertTriangle },
  { value: 'partial_outage', label: 'Partial Outage',      Icon: AlertTriangle },
  { value: 'investigating', label: 'Investigating',         Icon: AlertCircle },
  { value: 'identified',    label: 'Identified',           Icon: Target },
  { value: 'down',          label: 'Major Outage',         Icon: XCircle },
];

function timeAgo(ts: number | null): string {
  if (!ts) return '—';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function ForceSelect({ value, onChange }: {
  value: Service['forcedStatus'];
  onChange: (v: Service['forcedStatus']) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const current = FORCE_OPTIONS.find(o => o.value === (value ?? '')) ?? FORCE_OPTIONS[0];
  const CurrentIcon = current.Icon;

  return (
    <div className="force-sel" ref={ref}>
      <button
        type="button"
        className="force-sel-trigger"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
      >
        <CurrentIcon size={13} strokeWidth={2} />
        <span>{current.label}</span>
        <ChevronDown size={12} strokeWidth={2.5} className={open ? 'fsel-chevron open' : 'fsel-chevron'} />
      </button>
      {open && (
        <div className="force-sel-menu">
          {FORCE_OPTIONS.map(opt => {
            const OptIcon = opt.Icon;
            return (
              <button
                key={String(opt.value)}
                type="button"
                className={`force-sel-item${opt.value === (value ?? '') ? ' active' : ''}`}
                onClick={() => { onChange(opt.value); setOpen(false); }}
              >
                <OptIcon size={13} strokeWidth={2} />
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Bilingual message helpers ──────────────────────────────────────────────

function parseMsg(raw: string): { en: string; fr: string } {
  if (!raw) return { en: '', fr: '' };
  try {
    const p = JSON.parse(raw);
    if (p && typeof p === 'object') return { en: p.en || '', fr: p.fr || '' };
  } catch {}
  return { en: raw, fr: '' };
}

type PresetMessage = { label: string; en: string; fr: string };

const PRESET_MESSAGES: Record<string, PresetMessage[]> = {
  investigating: [
    { label: 'Detection (auto)',   en: 'Automated monitoring has detected an issue. Assessment is underway.',                                              fr: 'La surveillance automatisée a détecté un problème. L\'évaluation est en cours.' },
    { label: 'Team alerted',       en: 'We are currently investigating an issue. We will provide an update shortly.',                                     fr: 'Nous enquêtons actuellement sur un problème. Une mise à jour suivra sous peu.' },
    { label: 'Service disruption', en: 'We are currently experiencing a service disruption. Our team is working to identify the root cause.',             fr: 'Nous connaissons actuellement une perturbation de service. Notre équipe travaille à identifier la cause principale.' },
  ],
  identified: [
    { label: 'Root cause found', en: 'The root cause has been identified. A fix is being implemented.',                                                   fr: 'La cause principale a été identifiée. Un correctif est en cours de déploiement.' },
    { label: 'Fix in progress',  en: 'We have identified the issue and are deploying a fix. We will update you shortly.',                                 fr: 'Nous avons identifié le problème et déployons un correctif. Nous vous tiendrons informés sous peu.' },
  ],
  monitoring: [
    { label: 'Watching post-fix', en: 'A fix has been deployed. We are monitoring the situation closely.',                                               fr: 'Un correctif a été déployé. Nous surveillons la situation de près.' },
    { label: 'Stabilizing',       en: 'Services are recovering. We are monitoring to confirm full stability.',                                           fr: 'Les services se rétablissent. Nous surveillons pour confirmer la stabilité complète.' },
  ],
  degraded: [
    { label: 'Degraded perf',   en: 'Some services are experiencing degraded performance. Our team is working on a resolution.',                          fr: 'Certains services subissent des performances dégradées. Notre équipe travaille sur une résolution.' },
    { label: 'Slower than usual', en: 'Users may experience slower response times than usual. We are investigating.',                                    fr: 'Les utilisateurs peuvent constater des temps de réponse plus lents qu\'à l\'habitude. Nous enquêtons.' },
  ],
  partial_outage: [
    { label: 'Some components down', en: 'Some components are currently unavailable. We are investigating and will provide updates.',                     fr: 'Certains composants sont actuellement indisponibles. Nous enquêtons et fournirons des mises à jour.' },
    { label: 'Subset affected',      en: 'A subset of our services is currently experiencing an outage. Our team is actively investigating.',            fr: 'Un sous-ensemble de nos services connaît actuellement une panne. Notre équipe enquête activement.' },
  ],
  outage: [
    { label: 'Major outage',      en: 'We are experiencing a major service outage. We will update every 30 minutes.',                                    fr: 'Nous subissons une panne de service majeure. Nous vous tiendrons informés toutes les 30 minutes.' },
    { label: 'All systems down',  en: 'All services are currently unavailable. Our team is working urgently to restore service.',                        fr: 'Tous les services sont actuellement indisponibles. Notre équipe travaille en urgence pour rétablir le service.' },
  ],
  maintenance: [
    { label: 'Scheduled', en: 'Scheduled maintenance is in progress. Services may be intermittently unavailable.',                                       fr: 'Une maintenance planifiée est en cours. Les services peuvent être intermittents.' },
    { label: 'Routine',   en: 'Routine maintenance is being performed. We expect to be back online shortly.',                                            fr: 'Une maintenance de routine est en cours. Nous prévoyons être de retour en ligne sous peu.' },
  ],
  upgrade: [
    { label: 'System upgrade',  en: 'A system upgrade is underway. Some features may be temporarily unavailable.',                                       fr: 'Une mise à niveau du système est en cours. Certaines fonctionnalités peuvent être temporairement indisponibles.' },
    { label: 'Platform update', en: 'We are performing a platform update. Expect brief interruptions.',                                                  fr: 'Nous effectuons une mise à jour de la plateforme. Attendez-vous à de brèves interruptions.' },
  ],
  unavailable: [
    { label: 'Detection',          en: 'Automated monitoring has detected an issue. Assessment is underway.',                                            fr: 'La surveillance automatisée a détecté un problème. L\'évaluation est en cours.' },
    { label: 'Service unavailable', en: 'The service is currently unavailable. We are investigating.',                                                  fr: 'Le service est actuellement indisponible. Nous enquêtons.' },
  ],
};

function BilingualMessageInput({
  value,
  onChange,
  statusKey = '',
  placeholder = 'Message visible aux visiteurs…',
}: {
  value: string;
  onChange: (v: string) => void;
  statusKey?: string;
  placeholder?: string;
}) {
  const parsed = parseMsg(value);
  const [en, setEn] = React.useState(parsed.en);
  const [fr, setFr] = React.useState(parsed.fr);

  // Sync when an external preset or API load changes the value
  React.useEffect(() => {
    const p = parseMsg(value);
    setEn(p.en);
    setFr(p.fr);
  }, [value]);

  const commit = (newEn: string, newFr: string) => {
    if (!newEn && !newFr) { onChange(''); return; }
    onChange(JSON.stringify({ en: newEn, fr: newFr }));
  };

  const presets = PRESET_MESSAGES[statusKey] ?? [];

  return (
    <div className="bilingual-input">
      {presets.length > 0 && (
        <div className="bilingual-preset-bar">
          <span className="bilingual-preset-label">Quick fill:</span>
          {presets.map(p => (
            <button
              key={p.label}
              type="button"
              className="bilingual-preset-btn"
              onClick={() => { setEn(p.en); setFr(p.fr); commit(p.en, p.fr); }}
            >
              {p.label}
            </button>
          ))}
          {(en || fr) && (
            <button
              type="button"
              className="bilingual-preset-btn bilingual-preset-clear"
              onClick={() => { setEn(''); setFr(''); onChange(''); }}
            >
              ✕ Clear
            </button>
          )}
        </div>
      )}
      <div className="bilingual-input-row">
        <div className="bilingual-input-field">
          <span className="bilingual-lang-tag">EN</span>
          <input
            type="text"
            className="svc-detail-input"
            aria-label="English public message"
            placeholder={placeholder}
            value={en}
            onChange={e => setEn(e.target.value)}
            onBlur={() => commit(en, fr)}
          />
        </div>
        <div className="bilingual-input-field">
          <span className="bilingual-lang-tag">FR</span>
          <input
            type="text"
            className="svc-detail-input"
            aria-label="French public message"
            placeholder={placeholder}
            value={fr}
            onChange={e => setFr(e.target.value)}
            onBlur={() => commit(en, fr)}
          />
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { token, logout } = useAuth();

  const [services, setServices] = useState<Service[]>([]);
  const [health, setHealth] = useState<Record<string, HealthEntry>>({});
  const [discovered, setDiscovered] = useState<Discovered[]>([]);
  const [logs, setLogs] = useState<ProbeLog[]>([]);
  const [logPage, setLogPage] = useState(1);
  const [logLimit, setLogLimit] = useState(25);
  const [totalLogs, setTotalLogs] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState<number | null>(null);
  const [lastChecked, setLastChecked] = useState<number | null>(null);

  const [globalStatus, setGlobalStatus] = useState('operational');
  const [globalMessage, setGlobalMessage] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);

  const [checking, setChecking] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  const [newSvc, setNewSvc] = useState({ name: '', url: '', isPublic: true });
  const [adding, setAdding] = useState(false);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<Service['forcedStatus']>('');
  const [bulkMessage, setBulkMessage] = useState('');

  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const authHeaders = (): HeadersInit => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const handle401 = (res: Response) => {
    if (res.status === 401) { logout(); return true; }
    return false;
  };

  // ponytail: use refs to hold current page/limit so fetchLogs is a stable callback that
  // never goes stale. If we put logPage/logLimit in useCallback deps, every page change
  // recreates fetchAll → useEffect fires → interval restarts → old-value fetch overwrites
  // the correct result. Refs give us fresh values without causing re-renders of the closure.
  const logPageRef = useRef(1);
  const logLimitRef = useRef(25);
  logPageRef.current = logPage;
  logLimitRef.current = logLimit;

  const fetchLogs = useCallback(async (page?: number, limit?: number) => {
    const p = page ?? logPageRef.current;
    const l = limit ?? logLimitRef.current;
    setLogsLoading(true);
    try {
      const res = await fetch(`/api/status-page/logs?page=${p}&limit=${l}`, { headers: authHeaders() });
      if (handle401(res)) return;
      if (res.ok) {
        const data = await res.json();
        // ponytail: accept either paginated envelope { logs, total, page, limit, totalPages } or legacy flat array
        if (Array.isArray(data)) {
          setLogs(data);
          setTotalLogs(data.length);
          setTotalPages(Math.max(1, Math.ceil(data.length / l)));
        } else if (data && Array.isArray(data.logs)) {
          setLogs(data.logs);
          setTotalLogs(data.total ?? data.logs.length);
          setTotalPages(data.totalPages ?? Math.max(1, Math.ceil((data.total ?? data.logs.length) / l)));
        }
      }
    } catch (e) {
      console.warn('Logs load error', e);
    } finally {
      setLogsLoading(false);
    }
  }, [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [svcRes, healthRes, discRes, statRes] = await Promise.all([
        fetch('/api/status-page/services', { headers: authHeaders() }),
        fetch('/api/status-page/health', { headers: authHeaders() }),
        fetch('/api/status-page/discovered', { headers: authHeaders() }),
        fetch('/api/status-page/status')
      ]);
      if ([svcRes, healthRes, discRes, statRes].some(handle401)) return;
      if (svcRes.ok) setServices(await svcRes.json());
      if (healthRes.ok) {
        const data = await healthRes.json();
        const map: Record<string, HealthEntry> = {};
        (data.services || []).forEach((s: HealthEntry) => { map[s.id] = s; });
        setHealth(map);
        setLastChecked(data.checkedAt || null);
      }
      if (discRes.ok) setDiscovered(await discRes.json());
      if (statRes.ok) {
        const data = await statRes.json();
        setGlobalStatus(data.status || 'operational');
        setGlobalMessage(data.customMessage || '');
      }
      fetchLogs();
    } catch (e) {
      console.warn('Load error', e);
    }
  }, [token, fetchLogs]);

  const changeLogPage = (newPage: number) => {
    const p = Math.max(1, Math.min(newPage, totalPages));
    setLogPage(p);
    logPageRef.current = p;
    setExpandedLog(null);
    fetchLogs(p, logLimitRef.current);
  };

  const changeLogLimit = (newLimit: number) => {
    setLogLimit(newLimit);
    setLogPage(1);
    logLimitRef.current = newLimit;
    logPageRef.current = 1;
    setExpandedLog(null);
    fetchLogs(1, newLimit);
  };

  useEffect(() => {
    fetchAll();
    refreshTimer.current = setInterval(fetchAll, 15000);
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
  }, [fetchAll]);

  const flash = (msg: string, kind: 'ok' | 'err' = 'ok') => {
    setError(''); setInfo('');
    if (kind === 'ok') setInfo(msg); else setError(msg);
    setTimeout(() => { setError(''); setInfo(''); }, 3500);
  };

  const updateService = async (id: string, patch: Partial<Service>) => {
    const svc = services.find(s => s.id === id);
    if (!svc) return;
    setServices(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s));
    try {
      const res = await fetch(`/api/status-page/services/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ ...svc, ...patch })
      });
      if (handle401(res)) return;
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        flash(data.error || 'Save failed', 'err');
        fetchAll();
      }
    } catch {
      flash('Network error', 'err');
      fetchAll();
    }
  };

  const deleteService = async (id: string) => {
    if (!window.confirm('Delete this monitored service?')) return;
    try {
      const res = await fetch(`/api/status-page/services/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (handle401(res)) return;
      if (res.ok) {
        setServices(prev => prev.filter(s => s.id !== id));
        if (expandedId === id) setExpandedId(null);
        flash('Deleted');
      } else {
        flash('Delete failed', 'err');
      }
    } catch {
      flash('Network error', 'err');
    }
  };

  const trackDiscovered = async (host: string, isPublic: boolean) => {
    try {
      const res = await fetch('/api/status-page/discovered/track', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ hostname: host, isPublic })
      });
      if (handle401(res)) return;
      if (res.ok) { flash(`Now monitoring ${host}`); fetchAll(); }
      else {
        const data = await res.json().catch(() => ({}));
        flash(data.error || 'Failed to track', 'err');
      }
    } catch { flash('Network error', 'err'); }
  };

  const forgetDiscovered = async (host: string) => {
    try {
      const res = await fetch(`/api/status-page/discovered/${encodeURIComponent(host)}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (handle401(res)) return;
      if (res.ok) setDiscovered(prev => prev.filter(d => d.hostname !== host));
    } catch { flash('Network error', 'err'); }
  };

  const scanDns = async () => {
    setScanning(true);
    try {
      const res = await fetch('/api/status-page/discovered/scan', { method: 'POST', headers: authHeaders() });
      if (handle401(res)) return;
      if (res.ok) {
        const data = await res.json();
        const count = data.added?.length ?? 0;
        flash(count > 0 ? `DNS scan found ${count} new host(s)` : 'DNS scan complete — no new hosts');
        if (count > 0) fetchAll();
      } else {
        flash('DNS scan failed', 'err');
      }
    } catch { flash('Network error', 'err'); }
    finally { setScanning(false); }
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      const res = await fetch('/api/status-page/check-now', { method: 'POST', headers: authHeaders() });
      if (handle401(res)) return;
      if (res.ok) {
        flash('Checks triggered');
        setTimeout(() => {
          fetchAll();
          fetchLogs(1, logLimit);
        }, 1000);
      }
      else flash('Failed to trigger', 'err');
    } catch { flash('Network error', 'err'); }
    finally { setChecking(false); }
  };

  const saveGlobalStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingStatus(true);
    try {
      const res = await fetch('/api/status-page/status', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ status: globalStatus, customMessage: globalMessage })
      });
      if (handle401(res)) return;
      if (res.ok) flash('Global status saved');
      else flash('Save failed', 'err');
    } catch { flash('Network error', 'err'); }
    finally { setSavingStatus(false); }
  };

  const addService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSvc.name.trim() || !newSvc.url.trim()) { flash('Name and URL required', 'err'); return; }
    if (!/^https?:\/\//.test(newSvc.url)) { flash('URL must start with http:// or https://', 'err'); return; }
    setAdding(true);
    try {
      const res = await fetch('/api/status-page/services', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(newSvc)
      });
      if (handle401(res)) return;
      if (res.ok) {
        flash('Service added');
        setNewSvc({ name: '', url: '', isPublic: true });
        setShowAdd(false);
        fetchAll();
      } else {
        const data = await res.json().catch(() => ({}));
        flash(data.error || 'Add failed', 'err');
      }
    } catch { flash('Network error', 'err'); }
    finally { setAdding(false); }
  };

  const applyBulk = async () => {
    const ids = Array.from(selectedIds);
    const patch: Partial<Service> = { forcedStatus: bulkStatus };
    if (bulkMessage.trim()) patch.message = bulkMessage.trim();
    await Promise.all(ids.map(id => updateService(id, patch)));
    flash(`Updated ${ids.length} service(s)`);
    setSelectedIds(new Set());
    setBulkMessage('');
  };

  const summary = (() => {
    let ok = 0, deg = 0, down = 0;
    services.forEach(s => {
      const st = health[s.id]?.status || 'unknown';
      if (st === 'operational') ok++;
      else if (st === 'degraded') deg++;
      else if (st === 'down') down++;
    });
    return { ok, deg, down };
  })();

  const untrackedDiscovered = discovered.filter(d => !d.tracked);
  const allSelected = selectedIds.size === services.length && services.length > 0;

  return (
    <div className="admin">
      <header className="admin-top">
        <div className="admin-brand">
          <img src="/logo.png" alt="UDesign" />
          <div>
            <div className="admin-brand-name">UDesign Status</div>
            <div className="admin-brand-sub">Admin Control</div>
          </div>
        </div>
        <div className="admin-top-actions">
          <button type="button" className="sp-btn-ghost" onClick={scanDns} disabled={scanning}>
            <Radar size={15} aria-hidden="true" />
            {scanning ? 'Scanning…' : 'Scan DNS'}
          </button>
          <button type="button" className="sp-btn-ghost" onClick={checkNow} disabled={checking}>
            <RefreshCw size={15} className={checking ? 'is-spinning' : ''} aria-hidden="true" />
            {checking ? 'Checking…' : 'Check now'}
          </button>
          <button type="button" className="sp-btn-ghost" onClick={logout}><LogOut size={15} aria-hidden="true" />Log out</button>
        </div>
      </header>

      {(error || info) && (
        <div className={`admin-toast ${error ? 'err' : 'ok'}`} role="status" aria-live="polite">{error || info}</div>
      )}

      {/* Summary strip */}
      <section className="admin-summary" aria-label="Monitoring summary">
        <div className={`sum-card sum-card-${summary.down > 0 ? 'down' : summary.deg > 0 ? 'deg' : 'ok'}`}>
          <div className="sum-label">Overall</div>
          <div className="sum-value">
            {summary.down > 0 ? 'Incident' : summary.deg > 0 ? 'Degraded' : services.length > 0 ? 'Operational' : '—'}
          </div>
        </div>
        <div className="sum-card"><div className="sum-label">Operational</div><div className="sum-value">{summary.ok}</div></div>
        <div className="sum-card"><div className="sum-label">Degraded</div><div className="sum-value">{summary.deg}</div></div>
        <div className="sum-card"><div className="sum-label">Down</div><div className="sum-value">{summary.down}</div></div>
        <div className="sum-card"><div className="sum-label">Last check</div><div className="sum-value">{timeAgo(lastChecked)}</div></div>
      </section>

      {/* Global override */}
      <section className="admin-card">
        <div className="admin-card-head">
          <h2>Global page banner</h2>
          <p>Overrides the auto-computed status shown to visitors. Leave on "Operational" to let live checks decide.</p>
        </div>
        <form onSubmit={saveGlobalStatus} className="admin-banner-form">
          <div className="admin-form-row">
            <label className="sr-only" htmlFor="global-page-status">Global page status</label>
            <select id="global-page-status" value={globalStatus} onChange={e => setGlobalStatus(e.target.value)}>
              <option value="operational">Operational — use live data</option>
              <option value="investigating">Investigating</option>
              <option value="degraded">Degraded Performance</option>
              <option value="partial_outage">Partial Outage</option>
              <option value="outage">Major Outage</option>
              <option value="maintenance">Under Maintenance</option>
              <option value="upgrade">System Upgrade</option>
              <option value="unavailable">Unavailable</option>
            </select>
            <button type="submit" className="sp-btn-primary" disabled={savingStatus}>
              {savingStatus ? 'Saving…' : 'Save'}
            </button>
          </div>
          <div className="admin-form-msg-row">
            <BilingualMessageInput
              value={globalMessage}
              onChange={setGlobalMessage}
              statusKey={globalStatus}
              placeholder="Optional public message (overrides default copy)…"
            />
          </div>
        </form>
      </section>

      {/* Discovered routes prompting tracking */}
      {untrackedDiscovered.length > 0 && (
        <section className="admin-card">
          <div className="admin-card-head">
            <h2>New routes detected</h2>
            <p>The worker has handled these hostnames but they aren't being monitored yet.</p>
          </div>
          <ul className="disc-list">
            {untrackedDiscovered.map(d => (
              <li key={d.hostname}>
                <span className="disc-host">{d.hostname}</span>
                <span className="disc-actions">
                  <button type="button" className="sp-btn-ghost" onClick={() => trackDiscovered(d.hostname, false)}>Monitor privately</button>
                  <button type="button" className="sp-btn-primary" onClick={() => trackDiscovered(d.hostname, true)}>Monitor publicly</button>
                  <button type="button" className="sp-btn-icon" onClick={() => forgetDiscovered(d.hostname)} aria-label={`Forget ${d.hostname}`}><X size={15} aria-hidden="true" /></button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Main services list */}
      <section className="admin-card">
        <div className="admin-card-head admin-card-head-row">
          <div>
            <h2>Monitored routes</h2>
            <p>Click any row to expand controls. Toggle Public, set a message, or force a status for incidents and maintenance.</p>
          </div>
          <button type="button" className="sp-btn-primary" onClick={() => setShowAdd(v => !v)}>
            {showAdd ? <X size={15} aria-hidden="true" /> : <Plus size={15} aria-hidden="true" />}
            {showAdd ? 'Cancel' : 'Add service'}
          </button>
        </div>

        {showAdd && (
          <form onSubmit={addService} className="admin-add-form">
            <input
              type="text"
              aria-label="Service name"
              placeholder="Name (e.g. Billing API)"
              value={newSvc.name}
              onChange={e => setNewSvc(s => ({ ...s, name: e.target.value }))}
              required
            />
            <input
              type="url"
              aria-label="Service health URL"
              placeholder="https://example.com/health"
              value={newSvc.url}
              onChange={e => setNewSvc(s => ({ ...s, url: e.target.value }))}
              required
            />
            <label className="admin-inline-check">
              <input
                type="checkbox"
                checked={newSvc.isPublic}
                onChange={e => setNewSvc(s => ({ ...s, isPublic: e.target.checked }))}
              />
              Public
            </label>
            <button type="submit" className="sp-btn-primary" disabled={adding}>
              {adding ? 'Adding…' : 'Add'}
            </button>
          </form>
        )}

        {selectedIds.size > 0 && (
          <div className="admin-bulk-bar">
            <span className="bulk-count">{selectedIds.size} selected</span>
            <ForceSelect value={bulkStatus} onChange={v => setBulkStatus(v)} />
            <BilingualMessageInput
              value={bulkMessage}
              onChange={setBulkMessage}
              statusKey={String(bulkStatus || '')}
              placeholder="Public message (optional)…"
            />
            <button type="button" className="sp-btn-primary" onClick={applyBulk}>Apply to {selectedIds.size}</button>
            <button type="button" className="sp-btn-ghost" onClick={() => setSelectedIds(new Set())}>Cancel</button>
          </div>
        )}

        {services.length === 0 ? (
          <div className="admin-empty">No services configured yet. Add one above, or monitor a detected route.</div>
        ) : (
          <ul className="svc-list">
            {/* Select-all header row */}
            <li className="svc-list-header">
              <label className="checkbox-hit">
                <input
                  type="checkbox"
                  className="admin-cb"
                  aria-label="Select all services"
                  checked={allSelected}
                  onChange={e => setSelectedIds(e.target.checked ? new Set(services.map(s => s.id)) : new Set())}
                />
              </label>
              <span className="svc-hdr-service">Service</span>
              <span className="svc-hdr-status">Status</span>
              <span className="svc-hdr-meta">Latency · Uptime · Last check</span>
            </li>

            {services.map(svc => {
              const h = health[svc.id];
              const status = h?.status || 'unknown';
              const isExpanded = expandedId === svc.id;
              const isSelected = selectedIds.has(svc.id);
              const StatusIcon = STATUS_ICON[status] || HelpCircle;

              return (
                <li key={svc.id} className={`svc-item${isExpanded ? ' svc-expanded' : ''}${isSelected ? ' svc-selected' : ''}`}>
                  <div
                    className="svc-row"
                    onClick={() => setExpandedId(isExpanded ? null : svc.id)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpandedId(isExpanded ? null : svc.id);
                      }
                    }}
                  >
                    <div className="svc-cb" onClick={e => e.stopPropagation()}>
                      <label className="checkbox-hit">
                        <input
                          type="checkbox"
                          className="admin-cb"
                          aria-label={`Select ${svc.name}`}
                          checked={isSelected}
                          onChange={e => setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(svc.id); else next.delete(svc.id);
                            return next;
                          })}
                        />
                      </label>
                    </div>
                    <div className="svc-identity">
                      <div className="svc-name">{svc.name}</div>
                      <div className="svc-url">{svc.url}</div>
                    </div>
                    <div className="svc-status-col">
                      <span className={`sp-pill sp-pill-${status}`}>
                        <StatusIcon size={12} strokeWidth={2.5} />
                        {status}
                      </span>
                      {svc.forcedStatus && (
                        <span className="svc-forced-badge">forced</span>
                      )}
                    </div>
                    <div className="svc-metrics">
                      {h?.latency != null && <span>{h.latency}ms</span>}
                      {h?.uptime24h != null && <span>{h.uptime24h}%</span>}
                      {h?.checkedAt && <span>{timeAgo(h.checkedAt)}</span>}
                    </div>
                    <ChevronDown
                      size={16}
                      strokeWidth={2}
                      className={`svc-chevron${isExpanded ? ' open' : ''}`}
                    />
                  </div>

                  {isExpanded && (
                    <div className="svc-detail">
                      <div className="svc-detail-field">
                        <label className="svc-detail-label">Public</label>
                        <button
                          className={`toggle ${svc.isPublic !== false ? 'toggle-on' : ''}`}
                          type="button"
                          aria-label={`Show ${svc.name} publicly`}
                          aria-pressed={svc.isPublic !== false}
                          onClick={() => updateService(svc.id, { isPublic: !(svc.isPublic !== false) })}
                          title="Toggle public visibility"
                        >
                          <span className="toggle-knob" />
                        </button>
                      </div>
                      <div className="svc-detail-field svc-detail-grow">
                        <label className="svc-detail-label">Public message</label>
                        <BilingualMessageInput
                          value={svc.message || ''}
                          onChange={v => updateService(svc.id, { message: v })}
                          statusKey={svc.forcedStatus ?? ''}
                          placeholder="Optional note shown to visitors…"
                        />
                      </div>
                      <div className="svc-detail-field">
                        <label className="svc-detail-label">Force status</label>
                        <ForceSelect
                          value={svc.forcedStatus ?? ''}
                          onChange={v => updateService(svc.id, { forcedStatus: v })}
                        />
                      </div>
                      <div className="svc-detail-field">
                        <button type="button" className="svc-delete-btn" onClick={() => deleteService(svc.id)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Check logs */}
      <section className="admin-card">
        <div className="admin-card-head admin-card-head-row">
          <div>
            <h2>Check logs</h2>
            <p>Non-operational results and status changes from every health check run. Newest first.</p>
          </div>
          <div className="log-card-controls">
            <label className="sr-only" htmlFor="log-limit-select">Logs per page</label>
            <select
              id="log-limit-select"
              className="log-limit-select"
              value={logLimit}
              onChange={e => changeLogLimit(Number(e.target.value))}
              aria-label="Logs per page"
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
            <button
              type="button"
              className="sp-btn-ghost"
              onClick={() => fetchLogs(logPage, logLimit)}
              disabled={logsLoading}
              title="Refresh logs"
            >
              <RefreshCw size={14} className={logsLoading ? 'is-spinning' : ''} aria-hidden="true" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
        {logs.length === 0 ? (
          <div className="admin-empty">No log entries yet — logs appear after the next scheduled check.</div>
        ) : (
          <>
            <div className="log-scroll">
              <table className="log-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Service</th>
                    <th>Status</th>
                    <th>HTTP</th>
                    <th>Latency</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((entry, i) => {
                    const LogIcon = STATUS_ICON[entry.status] || HelpCircle;
                    const hasDetail = !!(entry.responseHeaders && Object.keys(entry.responseHeaders).length > 0) || !!entry.bodySnippet;
                    const isOpen = expandedLog === i;
                    return (
                      <React.Fragment key={i}>
                        <tr
                          className={`log-row log-row-${entry.status}${hasDetail ? ' log-row-clickable' : ''}`}
                          onClick={() => hasDetail && setExpandedLog(isOpen ? null : i)}
                          tabIndex={hasDetail ? 0 : undefined}
                          aria-expanded={hasDetail ? isOpen : undefined}
                          onKeyDown={e => {
                            if (hasDetail && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              setExpandedLog(isOpen ? null : i);
                            }
                          }}
                        >
                          <td className="log-time">{new Date(entry.t).toLocaleString()}</td>
                          <td>{entry.name}</td>
                          <td>
                            <span className={`sp-pill sp-pill-${entry.status}`}>
                              <LogIcon size={11} strokeWidth={2.5} />
                              {entry.status}
                            </span>
                            {entry.prevStatus && entry.prevStatus !== entry.status && (
                              <span className="log-prev"> ← {entry.prevStatus}</span>
                            )}
                          </td>
                          <td>{entry.httpStatus ?? '—'}</td>
                          <td>{entry.latency != null ? `${entry.latency}ms` : '—'}</td>
                          <td>
                            <span className="log-detail">{entry.detail}</span>
                            {hasDetail && <span className="log-expand-hint">{isOpen ? ' ▲' : ' ▼'}</span>}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="log-detail-row">
                            <td colSpan={6}>
                              <div className="log-detail-body">
                                {entry.responseHeaders && Object.keys(entry.responseHeaders).length > 0 && (
                                  <div className="log-detail-section">
                                    <div className="log-detail-label">Response headers</div>
                                    <pre className="log-pre">{Object.entries(entry.responseHeaders).map(([k, v]) => `${k}: ${v}`).join('\n')}</pre>
                                  </div>
                                )}
                                {entry.bodySnippet && (
                                  <div className="log-detail-section">
                                    <div className="log-detail-label">Response body {entry.bodySnippet.length === 512 ? '(first 512 chars)' : ''}</div>
                                    <pre className="log-pre">{entry.bodySnippet}</pre>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalLogs > 0 && (
              <div className="log-pagination">
                <span className="log-pagination-info">
                  Showing {(logPage - 1) * logLimit + 1}–{Math.min(logPage * logLimit, totalLogs)} of {totalLogs}
                </span>
                <div className="log-pagination-actions">
                  <button
                    type="button"
                    className="sp-btn-ghost log-page-btn"
                    onClick={() => changeLogPage(logPage - 1)}
                    disabled={logPage <= 1 || logsLoading}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={14} aria-hidden="true" />
                    <span>Prev</span>
                  </button>
                  <span className="log-page-current" aria-current="page">
                    {logPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    className="sp-btn-ghost log-page-btn"
                    onClick={() => changeLogPage(logPage + 1)}
                    disabled={logPage >= totalPages || logsLoading}
                    aria-label="Next page"
                  >
                    <span>Next</span>
                    <ChevronRight size={14} aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* All detected hostnames */}
      {discovered.length > 0 && (
        <section className="admin-card">
          <div className="admin-card-head">
            <h2>All detected hostnames</h2>
            <p>Every host the worker has proxied. Already monitored entries are marked.</p>
          </div>
          <ul className="disc-list">
            {discovered.map(d => (
              <li key={d.hostname}>
                <span className="disc-host">
                  {d.tracked && <CheckCircle2 size={13} strokeWidth={2.5} className="disc-tracked-icon" />}
                  {d.hostname}
                </span>
                <span className="disc-actions">
                  <button type="button" className="sp-btn-icon" onClick={() => forgetDiscovered(d.hostname)} aria-label={`Forget ${d.hostname}`}><X size={15} aria-hidden="true" /></button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
